import { useState, useEffect, useCallback } from "react";
import { Users, CheckCircle, XCircle, Clock, Copy, ExternalLink, ChevronDown, ChevronUp, RefreshCw, Mail, AlertCircle, Send } from "lucide-react";

const STATUS_LABELS = {
  pending_review: { label: "Pendente", color: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30" },
  approved: { label: "Aprovado", color: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" },
  rejected: { label: "Rejeitado", color: "text-red-400 bg-red-400/10 border-red-400/30" },
};

const PARTNER_TYPE_LABELS = {
  influencer: "Influencer",
  seller: "Vendedor",
  agency: "Agência",
  consultant: "Consultor",
  other: "Outro",
};

function EmailStatusBadge({ app, showId = false }) {
  if (app.status !== "approved") return null;
  if (app.onboarding_email_sent) {
    const sentAt = app.onboarding_sent_at
      ? new Date(app.onboarding_sent_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
      : "";
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"
        title={app.onboarding_email_provider_id ? `Resend ID: ${app.onboarding_email_provider_id}` : undefined}
      >
        <Mail className="w-3 h-3" />
        ✅ Email enviado{sentAt ? ` · ${sentAt}` : ""}
        {showId && app.onboarding_email_provider_id && (
          <span className="text-emerald-400 font-mono ml-1">{app.onboarding_email_provider_id.slice(0, 8)}</span>
        )}
      </span>
    );
  }
  if (app.onboarding_email_error) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"
        title={`Erro: ${app.onboarding_email_error}`}
      >
        <AlertCircle className="w-3 h-3" />
        ❌ Email falhou
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-400 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">
      <Clock className="w-3 h-3" />
      Não enviado
    </span>
  );
}

export function PartnersApplicationsTab({ adminAxios }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState("pending_review");
  const [expanded, setExpanded] = useState(null);
  const [acting, setActing] = useState(null);
  const [copied, setCopied] = useState(null);
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkSubject, setBulkSubject] = useState("");
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkTarget, setBulkTarget] = useState("approved");
  const [bulkTestEmail, setBulkTestEmail] = useState("");
  const [bulkSpecificEmail, setBulkSpecificEmail] = useState("");
  const [bulkCount, setBulkCount] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await adminAxios.get(`/affiliate/admin/partner-applications?status=${filter}`);
      setApps(res.data || []);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || "Erro desconhecido";
      setLoadError(`${status ? `HTTP ${status}: ` : ""}${detail}`);
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, [adminAxios, filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm("Aprovar esta candidatura?")) return;
    setActing(id + "_approve");
    try {
      const res = await adminAxios.post(`/affiliate/admin/partner-applications/${id}/approve`);
      if (res.data?.email_sent === false) {
        alert(`Aprovado, mas o email falhou: ${res.data?.email_error || "erro desconhecido"}. Use "Reenviar onboarding".`);
      }
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao aprovar");
    } finally {
      setActing(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Motivo da rejeição (opcional):");
    if (reason === null) return;
    setActing(id + "_reject");
    try {
      await adminAxios.post(`/affiliate/admin/partner-applications/${id}/reject`, { reason });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao rejeitar");
    } finally {
      setActing(null);
    }
  };

  const resendOnboarding = async (id) => {
    if (!window.confirm("Reenviar email de onboarding para este afiliado?")) return;
    setActing(id + "_resend");
    try {
      await adminAxios.post(`/affiliate/admin/partner-applications/${id}/resend-onboarding`);
      alert("Email reenviado com sucesso!");
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao reenviar");
    } finally {
      setActing(null);
    }
  };

  const copyLink = (link, id) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const loadTemplate = async () => {
    try {
      const res = await adminAxios.get("/platform-admin/affiliates/bulk-email/template");
      setBulkSubject(res.data.subject || "");
      setBulkMessage(res.data.message || "");
    } catch {
      setBulkSubject("Welcome to Kaiso System — Start building recurring income today");
      setBulkMessage(
        "Hello,\n\nWelcome to Kaiso System.\n\nYou are now part of one of the fastest-growing technology platforms for restaurants in Europe.\n\nOur goal is simple: to build a network of partners who grow together with the platform and generate real, recurring income.\n\nHOW IT WORKS FOR YOU\n\n– You bring restaurants\n– We provide the full system\n– You earn monthly recurring commissions\n\nThis means you build a client base that generates income every single month, without needing to resell to the same client.\n\nACCESS THE SYSTEM NOW (LIVE DEMO)\n\nSystem access:\nhttps://demo.kaisosystem.com/demo-ff5a3e\n\nLogin:\nhttps://app.kaisosystem.com/login\n\nEmail:\ndemo-ff5a3e@kaisodemo.com\n\nPassword:\nzxC6JBSc1U\n\nRecommendation: log in now, explore the platform and understand how it works. This will make it much easier to present it to restaurants.\n\nIMPORTANT — SET UP YOUR PAYMENTS\n\nWhen you register your first client, it is essential that you connect your account to receive commissions automatically.\n\nInside your affiliate area, click on the partner/Stripe link.\nCreate or connect your Stripe account.\nComplete the setup.\n\nThis step is mandatory to receive your recurring payments automatically every month.\n\nWithout this setup, the system cannot send your commissions.\n\nREAL EARNINGS EXAMPLE\n\nEnterprise plan: 189€ per restaurant\nCommission: 20%\n\n37.80€ per restaurant / month\n\nPotential:\n\n10 restaurants → 378€ / month\n20 restaurants → 756€ / month\n30 restaurants → 1,134€ / month\n50 restaurants → 1,890€ / month\n\nThis income is recurring.\n\nPERFORMANCE BONUSES\n\n30 active restaurants:\n+2,000€ bonus\n\n50 active restaurants:\n+3,000€ bonus\n\nLIVE PLATFORM\n\nhttps://kaisosystem.com/\n\nYou can use this link to show restaurants how the system works in real conditions.\n\nSUPPORT\n\nIf you need help closing your first deals:\n\nsuporte@kaisosystem.com\n\nIMPORTANT DEADLINE\n\nPartners who do not generate any sales within the first 30 days will have their account and data automatically removed from the system.\n\nThis ensures that our network is made up of active and committed partners.\n\nSTART NOW\n\nAccess the demo, understand the system, and take action today.\n\nThe faster you start, the faster you build your recurring income.\n\nWe are here to help you grow.\n\nWelcome to growth.\n\nKaiso System"
      );
    }
  };

  useEffect(() => {
    if (!bulkModal) return;
    if (bulkTarget === "test" || bulkTarget === "specific") { setBulkCount(1); return; }
    setBulkCount(null);
    adminAxios.get(`/platform-admin/affiliates/bulk-email/count?target=${bulkTarget}`)
      .then(r => setBulkCount(r.data.count))
      .catch(() => setBulkCount(null));
  }, [bulkModal, bulkTarget, adminAxios]);

  const sendBulkEmail = async () => {
    if (!bulkSubject.trim() || !bulkMessage.trim()) return;
    const isMass = !["test", "specific"].includes(bulkTarget);
    if (isMass && bulkCount > 0 && !window.confirm(`Enviar para ${bulkCount} destinatário(s)?\nAssunto: "${bulkSubject}"`)) return;
    setBulkSending(true);
    setBulkResult(null);
    try {
      const payload = { type: "campaign", subject: bulkSubject.trim(), message: bulkMessage.trim(), target: bulkTarget };
      if (bulkTarget === "test") payload.test_email = bulkTestEmail.trim() || undefined;
      if (bulkTarget === "specific") payload.specific_email = bulkSpecificEmail.trim();
      const res = await adminAxios.post("/platform-admin/affiliates/bulk-email", payload);
      const isTest = ["test", "specific"].includes(bulkTarget);
      const extra = res.data.resend_id ? ` (Resend ID: ${res.data.resend_id})` : "";
      setBulkResult({ ok: true, message: res.data.message + (isTest ? extra : "") });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Erro ao enviar";
      setBulkResult({ ok: false, message: detail });
    } finally {
      setBulkSending(false);
    }
  };

  const counts = { pending: apps.filter((a) => a.status === "pending_review").length };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            Candidaturas de Parceiros
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Representantes Comerciais / Afiliados independentes</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setBulkModal(true); setBulkResult(null); }}
            className="flex items-center gap-1.5 text-xs text-white bg-orange-500 hover:bg-orange-600 rounded-lg px-3 py-1.5 transition-colors font-semibold"
          >
            <Send className="w-3.5 h-3.5" />
            Enviar Email para Afiliados
          </button>
          <button onClick={load} className="text-xs text-emerald-600 border border-emerald-200 rounded-lg px-3 py-1.5 hover:bg-emerald-50 transition-colors">
            Atualizar
          </button>
        </div>
      </div>

      {/* Bulk Email Modal */}
      {bulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={(e) => e.target === e.currentTarget && setBulkModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-orange-500" />
                Enviar Email para Afiliados
              </h3>
              <button onClick={() => setBulkModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>

            {/* Target selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Modo de envio</label>
              <select
                value={bulkTarget}
                onChange={(e) => { setBulkTarget(e.target.value); setBulkResult(null); }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
              >
                <option value="test">🧪 Teste (apenas 1 email)</option>
                <option value="approved">✅ Afiliados aprovados</option>
                <option value="pending">⏳ Candidatos pendentes</option>
                <option value="rejected">❌ Candidatos rejeitados</option>
                <option value="all">📋 Todos (aprovados + pendentes + rejeitados)</option>
                <option value="specific">✉️ Email específico</option>
              </select>
            </div>

            {/* Conditional email inputs */}
            {bulkTarget === "test" && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email de teste <span className="font-normal text-gray-400">(deixe em branco para usar o seu email de admin)</span></label>
                <input
                  value={bulkTestEmail}
                  onChange={(e) => setBulkTestEmail(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                  placeholder="email@exemplo.com"
                  type="email"
                />
              </div>
            )}
            {bulkTarget === "specific" && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email específico</label>
                <input
                  value={bulkSpecificEmail}
                  onChange={(e) => setBulkSpecificEmail(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                  placeholder="email@exemplo.com"
                  type="email"
                />
              </div>
            )}

            {/* Recipient preview */}
            <div className={`text-sm rounded-lg px-3 py-2 flex items-center gap-2 ${bulkTarget === "test" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-gray-50 text-gray-600 border border-gray-200"}`}>
              <Mail className="w-4 h-4 shrink-0" />
              {bulkTarget === "test"
                ? "Este email será enviado para 1 destinatário (modo teste — sem deduplicação)."
                : bulkTarget === "specific"
                ? "Este email será enviado para 1 destinatário específico."
                : bulkCount === null
                ? "Calculando destinatários..."
                : `Este email será enviado para ${bulkCount} destinatário(s).`}
            </div>

            <button
              onClick={loadTemplate}
              className="text-xs text-orange-600 border border-orange-200 rounded-lg px-3 py-1.5 hover:bg-orange-50 transition-colors"
            >
              Usar template padrão de boas-vindas
            </button>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Assunto</label>
              <input
                value={bulkSubject}
                onChange={(e) => setBulkSubject(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                placeholder="Assunto do email"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Mensagem</label>
              <textarea
                value={bulkMessage}
                onChange={(e) => setBulkMessage(e.target.value)}
                rows={8}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-y font-mono"
                placeholder="Conteúdo do email"
              />
            </div>

            {bulkResult && (
              <div className={`text-sm rounded-lg px-3 py-2 ${bulkResult.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                {bulkResult.ok ? `✅ ${bulkResult.message}` : `❌ ${bulkResult.message}`}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setBulkModal(false)} className="text-sm text-gray-500 border border-gray-200 rounded-lg px-4 py-2 hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={sendBulkEmail}
                disabled={bulkSending || !bulkSubject.trim() || !bulkMessage.trim() || (bulkTarget === "specific" && !bulkSpecificEmail.trim())}
                className={`text-sm text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${bulkTarget === "test" ? "bg-blue-500 hover:bg-blue-600" : "bg-orange-500 hover:bg-orange-600"}`}
              >
                {bulkSending
                  ? "Enviando..."
                  : bulkTarget === "test"
                  ? <><Send className="w-3.5 h-3.5" />Enviar teste</>
                  : <><Send className="w-3.5 h-3.5" />Enviar para {bulkCount ?? "..."} afiliado(s)</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b">
        {[
          { id: "pending_review", label: "Pendentes" },
          { id: "approved", label: "Aprovados" },
          { id: "rejected", label: "Rejeitados" },
          { id: "all", label: "Todos" },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${filter === f.id ? "border-emerald-500 text-emerald-600" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
            {f.label}
            {f.id === "pending_review" && counts.pending > 0 && (
              <span className="ml-1.5 bg-yellow-400 text-yellow-900 text-xs font-bold px-1.5 py-0.5 rounded-full">{counts.pending}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Carregando...</div>
      ) : loadError ? (
        <div className="text-center py-16 text-red-500">
          <p className="font-semibold mb-1">Erro ao carregar candidaturas</p>
          <p className="text-xs font-mono bg-red-50 border border-red-200 rounded px-3 py-2 inline-block">{loadError}</p>
        </div>
      ) : apps.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Nenhuma candidatura encontrada</p>
        </div>
      ) : (
        <div className="space-y-3">
          {apps.map((app) => {
            const isOpen = expanded === app.id;
            const statusStyle = STATUS_LABELS[app.status] || STATUS_LABELS.pending_review;
            return (
              <div key={app.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpanded(isOpen ? null : app.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{app.full_name}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusStyle.color}`}>
                        {statusStyle.label}
                      </span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                        {PARTNER_TYPE_LABELS[app.partner_type] || app.partner_type}
                      </span>
                      <EmailStatusBadge app={app} />
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                      <span>{app.email}</span>
                      <span>{app.whatsapp}</span>
                      <span>📍 {app.city}, {app.country}</span>
                      <span>🌐 {app.language}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-gray-400">{new Date(app.created_at).toLocaleDateString("pt-PT")}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                      <Detail label="Redes Sociais" value={app.social_media || "—"} />
                      <Detail label="Já vendeu software?" value={app.has_sold_software ? "✅ Sim" : "❌ Não"} />
                      <Detail label="Contato com restaurantes?" value={app.has_restaurant_contacts ? "✅ Sim" : "❌ Não"} />
                      <Detail label="Indicações/mês" value={app.monthly_referrals_estimate} />
                      <Detail label="Recebimento" value={app.payment_method} />
                      <Detail label="Dados de recebimento" value={app.payment_details || "—"} />
                    </div>

                    {app.motivation && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Motivação</p>
                        <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200 whitespace-pre-line">{app.motivation}</p>
                      </div>
                    )}

                    {app.status === "approved" && app.referral_link && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <p className="text-xs font-semibold text-emerald-700">Link de parceiro · Código: <strong>{app.referral_code}</strong></p>
                          <EmailStatusBadge app={app} showId />
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-white border border-emerald-200 rounded-lg px-3 py-2 text-emerald-800 font-mono truncate">
                            {app.referral_link}
                          </code>
                          <button onClick={() => copyLink(app.referral_link, app.id)}
                            className="flex items-center gap-1.5 text-xs bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex-shrink-0">
                            <Copy className="w-3 h-3" />
                            {copied === app.id ? "Copiado!" : "Copiar"}
                          </button>
                          <a href={app.referral_link} target="_blank" rel="noreferrer" className="p-2 text-emerald-600 hover:text-emerald-800 flex-shrink-0">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>

                        {app.onboarding_email_error && (
                          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-1">
                            <p className="font-semibold">❌ Erro no envio do email:</p>
                            <p className="font-mono break-all">{app.onboarding_email_error}</p>
                            <p className="text-red-500 mt-1">Clique em "Reenviar onboarding" para tentar novamente.</p>
                          </div>
                        )}

                        {app.onboarding_email_provider_id && app.onboarding_email_sent && (
                          <p className="text-xs text-gray-400 font-mono">
                            Resend ID: {app.onboarding_email_provider_id}
                          </p>
                        )}

                        {app.last_resend_at && (
                          <p className="text-xs text-gray-400">
                            Último reenvio: {new Date(app.last_resend_at).toLocaleString("pt-PT")} por {app.last_resend_by}
                          </p>
                        )}

                        <button onClick={() => resendOnboarding(app.id)} disabled={!!acting}
                          className="flex items-center gap-2 text-xs border border-emerald-300 text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 px-4 py-2 rounded-lg transition-colors font-semibold">
                          <RefreshCw className={`w-3.5 h-3.5 ${acting === app.id + "_resend" ? "animate-spin" : ""}`} />
                          {acting === app.id + "_resend" ? "Enviando..." : "Reenviar onboarding"}
                        </button>
                      </div>
                    )}

                    {app.status === "rejected" && app.reject_reason && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-red-700 mb-1">Motivo da rejeição</p>
                        <p className="text-sm text-red-600">{app.reject_reason}</p>
                      </div>
                    )}

                    {app.status === "pending_review" && (
                      <div className="flex gap-3 pt-2">
                        <button onClick={() => approve(app.id)} disabled={!!acting}
                          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
                          <CheckCircle className="w-4 h-4" />
                          {acting === app.id + "_approve" ? "Aprovando..." : "Aprovar"}
                        </button>
                        <button onClick={() => reject(app.id)} disabled={!!acting}
                          className="flex items-center gap-2 border border-red-200 hover:bg-red-50 disabled:opacity-60 text-red-600 text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
                          <XCircle className="w-4 h-4" />
                          {acting === app.id + "_reject" ? "Rejeitando..." : "Rejeitar"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-700">{value}</p>
    </div>
  );
}
